/**
 * Tailwind CSS implementation of Stack component
 * Uses Seeds stack CSS classes from stack.css
 *
 * Handles only scalar space/align/direction values. Spacing is applied via the
 * flex `gap` property (replacing the per-child margin Box wrappers of the
 * styled-components path). Responsive (array) / arbitrary-string values and any
 * styled-system props route to the styled-components path via StackHybrid.
 */

import React from "react";
import type { TypeStackProps } from "./StackTypes";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const StackTailwind = React.forwardRef<HTMLDivElement, TypeStackProps>(
  (
    {
      children,
      space = 300,
      align = "left",
      direction = "vertical",
      as,
      className,
      ...rest
    }: TypeStackProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    // Render the consumer-provided element (e.g. <Stack as="section">) and
    // default to a div. `as` is a documented Stack prop (via TypeBoxProps) but
    // is not a styled-system prop, so scalar usage routes here rather than to
    // the styled-components path.
    const Component = (as || "div") as any;

    const classes = cn(
      "seeds-stack",
      `seeds-stack-${direction}`,
      `seeds-stack-align-${align}`,
      `seeds-stack-space-${space}`,
      className
    );

    // `as` has been destructured out, so the remaining props are plain
    // DOM/aria/data attributes spread onto the chosen element.
    return (
      <Component className={classes} ref={ref} {...rest}>
        {children}
      </Component>
    );
  }
);

StackTailwind.displayName = "StackTailwind";
